import { type CSSProperties, useState } from 'react';

export interface ToggleSwitchProps {
  /** Whether the toggle is checked/enabled */
  checked: boolean;
  /** Handler called when the toggle changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Whether the toggle is in loading state */
  loading?: boolean;
  /** Optional label for accessibility */
  label?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ToggleSwitch component for ON/OFF states.
 *
 * Features:
 * - Smooth animations (200ms transition)
 * - Loading state with spinner overlay
 * - Keyboard accessible (Space/Enter to toggle)
 * - ARIA attributes for screen readers
 * - Brand colors: #510CFF (checked), #E1E2E6 (unchecked)
 * - Disabled and loading states
 */
export const ToggleSwitch = ({
  checked,
  onChange,
  disabled = false,
  loading = false,
  label,
  size = 'md',
}: ToggleSwitchProps) => {
  const [isFocused, setIsFocused] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: { width: 36, height: 20, knobSize: 16, padding: 2 },
    md: { width: 48, height: 24, knobSize: 20, padding: 2 },
    lg: { width: 60, height: 30, knobSize: 26, padding: 2 },
  };

  const config = sizeConfig[size];
  const isInteractive = !disabled && !loading;

  const handleClick = () => {
    if (isInteractive) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isInteractive && (e.key === ' ' || e.key === 'Enter')) {
      e.preventDefault();
      onChange(!checked);
    }
  };

  const trackStyle: CSSProperties = {
    width: `${config.width}px`,
    height: `${config.height}px`,
    borderRadius: `${config.height / 2}px`,
    backgroundColor: checked ? '#510CFF' : '#E1E2E6',
    position: 'relative',
    cursor: isInteractive ? 'pointer' : 'not-allowed',
    opacity: disabled ? 0.5 : 1,
    transition: 'background-color 200ms ease-in-out, opacity 200ms ease-in-out',
    outline: isFocused ? `2px solid #510CFF` : 'none',
    outlineOffset: '2px',
  };

  const knobStyle: CSSProperties = {
    position: 'absolute',
    width: `${config.knobSize}px`,
    height: `${config.knobSize}px`,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    top: `${config.padding}px`,
    left: checked
      ? `${config.width - config.knobSize - config.padding}px`
      : `${config.padding}px`,
    transition: 'left 200ms ease-in-out',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  };

  const loadingOverlayStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: `${config.height / 2}px`,
  };

  const spinnerStyle: CSSProperties = {
    width: `${config.knobSize * 0.6}px`,
    height: `${config.knobSize * 0.6}px`,
    border: '2px solid #FFFFFF',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 600ms linear infinite',
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <div
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-disabled={!isInteractive}
        tabIndex={isInteractive ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={trackStyle}
      >
        {/* Knob */}
        <div style={knobStyle} />

        {/* Loading overlay */}
        {loading && (
          <div style={loadingOverlayStyle}>
            <div style={spinnerStyle} />
          </div>
        )}
      </div>

      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};
